import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { useUser } from '@/modules/shared/context/UserContext'
import { useQueryMyServiceAccounts } from '@/modules/shared/query-client/useServiceAccountAdmin'

// Lista dozwolonych adresów IP firmy. Pusta = dostęp z każdego adresu.
// Global admin: każda firma; właściciel konta usługowego: konto i jego firmy; właściciel firmy: ta firma.
export interface CompanyIpAllowList {
  companyId: string
  companyName: string
  isServiceCompany: boolean
  ranges: string[]
  currentIp: string | null
  serviceAccountName: string | null
  serviceAccountRanges: string[]
}

const ipAllowListQueryKey = (companyId: string) =>
  ['config.company', companyId, 'ipAllowList'] as const

export function useQueryCompanyIpAllowList(companyId: string | null | undefined) {
  return useQuery<CompanyIpAllowList>({
    queryKey: ipAllowListQueryKey(companyId ?? ''),
    queryFn: async () => {
      const res = await axiosInstance.get(`/config-api/company/${companyId}/ip-allow-list`)
      return res.data
    },
    enabled: !!companyId,
  })
}

export function useMutationCompanyIpAllowList(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation<CompanyIpAllowList, unknown, { ranges: string[] }>({
    mutationFn: async ({ ranges }) => {
      const res = await axiosInstance.post(
        `/config-api/company/${companyId}/ip-allow-list`,
        { ranges },
      )
      return res.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(ipAllowListQueryKey(companyId), data)
    },
  })
}

const COMPANY_OWNER_ROLE = 'Company Owner'

// Podpowiedź dla UI (pokazanie zakładki); serwer i tak sprawdza uprawnienie przy GET/POST.
export function useCanManageIpAllowList(companyId: string | null | undefined) {
  const { isAdmin, accountData } = useUser()
  const { data: myAccounts } = useQueryMyServiceAccounts({
    enabled: !!companyId && !!accountData?.hasServiceAccountAccess,
  })
  if (!companyId) return false
  if (isAdmin()) return true

  const ownsCompany = !!accountData?.roles
    .find((r) => String(r.companyId) === companyId)
    ?.roles.some((r) => r.name === COMPANY_OWNER_ROLE)
  const ownsServiceAccount = !!myAccounts?.some(
    (a) => a.isOwner && (a.id === companyId || a.children.some((c) => c.id === companyId)),
  )
  return ownsCompany || ownsServiceAccount
}
